import React, { FC } from "react";
import { Box, BoxProps } from "@mui/system";
import ResponsiveImage from "./ResponsiveImage";

export type SectionProps = BoxProps & {
  fillHeight?: boolean;
  inverted?: boolean;
  withPadding?: boolean;
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundImageMobile?: string;
};

/**
 * A functional component that represents a section of content with optional styles and a background image.
 *
 * @param {boolean} [fillHeight=false] - Determines whether the section should fill the full height of the viewport.
 * @param {boolean} [inverted=false] - Determines whether the text color and background color should be inverted.
 * If true, the text color will be set to "text.secondary" and the background color will be set to "text.primary".
 * If false, the text color will be set to "text.primary" and the background color will be set to "background.default".
 * @param {boolean} [withPadding=false] - Determines whether the section should have padding applied.
 * @param {string} [backgroundColor] - The background color for the section. This prop is ignored if `inverted` is set to true.
 * @param {string} [backgroundImage] - The filename (without the extension) for the background image of the section.
 */

const Section: FC<SectionProps> = ({
  fillHeight,
  inverted,
  withPadding = false,
  sx,
  backgroundColor,
  backgroundImage,
  backgroundImageMobile,
  children,
  ...props
}) => {
  const defaultHeight = props.height ?? "auto";
  const invertedBackgroundColor = inverted
    ? "text.primary"
    : "background.default";
  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: backgroundColor ?? invertedBackgroundColor,
        color: inverted ? "text.secondary" : "text.primary",
        maxWidth: "100vw",
        overflow: "hidden",
      }}
      {...props}
    >
      {backgroundImage && (
        <ResponsiveImage
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "100%",
          }}
          breakpoint="md"
          imgStyle={{ objectFit: "cover" }}
          src={backgroundImage}
          srcSmall={
            backgroundImageMobile ? backgroundImageMobile : backgroundImage
          }
          alt="backgroundImage"
        />
      )}
      <Box
        sx={{
          height: fillHeight ? "100vh" : defaultHeight,
          px: withPadding ? { xs: 2, md: 6 } : 0,
          py: withPadding ? { xs: 10, md: 15 } : 0,
          position: "relative",
          ...sx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Section;
